import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Icon from "@mdi/react";
import {
  mdiCalendarOutline,
  mdiAccountOutline,
  mdiMapMarkerOutline,
} from "@mdi/js";
import { setNotes, confirmBooking } from "../slice";
import { formatDate, formatTime } from "../utils";

const StepConfirmBooking = () => {
  const dispatch = useDispatch();
  const { uniqueId } = useParams();

  const {
    contactForm,
    agentForm,
    showAgentInfo,
    propertyDetailsForm,
    selectedDate,
    selectedTimeslot,
    selectedTimezone,
    notes,
    confirmLoading,
  } = useSelector((state) => state.publicClientBooking);

  const hasAgent =
    showAgentInfo &&
    (agentForm.agentName || agentForm.agentPhone || agentForm.agentEmail);

  const addressLine1 = [contactForm.address1, contactForm.address2]
    .filter(Boolean)
    .join(", ");
  const addressLine2 = [
    contactForm.city,
    contactForm.state,
    contactForm.postalCode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="public-client-booking-form-container">
      {/* Date & Time */}
      <div className="public-client-booking-confirm-datetime">
        <div className="public-client-booking-confirm-datetime-icon">
          <Icon path={mdiCalendarOutline} size={1} />
        </div>
        <div>
          <div className="public-client-booking-confirm-datetime-date">
            {formatDate(selectedDate, selectedTimezone)}
          </div>
          <div className="public-client-booking-confirm-datetime-time">
            {selectedTimeslot
              ? `${formatTime(selectedTimeslot.start_time, selectedTimezone)} - ${formatTime(selectedTimeslot.end_time, selectedTimezone)}`
              : ""}
          </div>
        </div>
      </div>

      <hr className="public-client-booking-confirm-divider" />

      {/* Contact & Address */}
      <div className="public-client-booking-confirm-section">
        <div className="public-client-booking-section-title">
          Contact &amp; Address
        </div>
        <div className="public-client-booking-confirm-contact-row">
          <div className="public-client-booking-confirm-contact-col">
            <Icon
              path={mdiAccountOutline}
              size={0.875}
              className="public-client-booking-confirm-inline-icon"
            />
            <div>
              <div className="public-client-booking-confirm-field-value">
                {contactForm.name}
              </div>
              <div className="public-client-booking-confirm-field-meta-row">
                {contactForm.phone && <span>{contactForm.phone}</span>}
                {contactForm.email && <span>{contactForm.email}</span>}
              </div>
            </div>
          </div>
          <div className="public-client-booking-confirm-contact-col">
            <Icon
              path={mdiMapMarkerOutline}
              size={0.875}
              className="public-client-booking-confirm-inline-icon"
            />
            <div>
              <div className="public-client-booking-confirm-field-value">
                {addressLine1}
              </div>
              <div className="public-client-booking-confirm-field-meta">
                {addressLine2}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Agent (only if provided in Step 1) */}
      {hasAgent && (
        <>
          <hr className="public-client-booking-confirm-divider" />
          <div className="public-client-booking-confirm-section">
            <div className="public-client-booking-section-title">Agent</div>
            <div className="public-client-booking-confirm-agent-row">
              <Icon
                path={mdiAccountOutline}
                size={0.875}
                className="public-client-booking-confirm-inline-icon"
              />
              <div>
                {agentForm.agentName && (
                  <div className="public-client-booking-confirm-field-value">
                    {agentForm.agentName}
                  </div>
                )}
                <div className="public-client-booking-confirm-field-meta-row">
                  {agentForm.agentPhone && <span>{agentForm.agentPhone}</span>}
                  {agentForm.agentEmail && <span>{agentForm.agentEmail}</span>}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Property Details */}
      <hr className="public-client-booking-confirm-divider" />
      <div className="public-client-booking-confirm-section">
        <div className="public-client-booking-section-title">
          Property Details
        </div>
        <div className="public-client-booking-confirm-property-grid">
          <div className="public-client-booking-confirm-property-row">
            <div className="public-client-booking-confirm-property-item">
              <span className="public-client-booking-confirm-property-label">
                Square Feet
              </span>
              <span className="public-client-booking-confirm-property-value">
                {propertyDetailsForm.squareFeet
                  ? Number(propertyDetailsForm.squareFeet).toLocaleString()
                  : ""}
              </span>
            </div>
            <div className="public-client-booking-confirm-property-item">
              <span className="public-client-booking-confirm-property-label">
                Year Built
              </span>
              <span className="public-client-booking-confirm-property-value">
                {propertyDetailsForm.yearBuilt || ""}
              </span>
            </div>
          </div>
          <div className="public-client-booking-confirm-property-row">
            <div className="public-client-booking-confirm-property-item">
              <span className="public-client-booking-confirm-property-label">
                Number of Stories
              </span>
              <span className="public-client-booking-confirm-property-value">
                {propertyDetailsForm.numberOfStories || ""}
              </span>
            </div>
            <div className="public-client-booking-confirm-property-item">
              <span className="public-client-booking-confirm-property-label">
                House Type
              </span>
              <span className="public-client-booking-confirm-property-value">
                {propertyDetailsForm.houseType || ""}
              </span>
            </div>
          </div>
          {propertyDetailsForm.parcelNumber && (
            <div className="public-client-booking-confirm-property-row">
              <div className="public-client-booking-confirm-property-item">
                <span className="public-client-booking-confirm-property-label">
                  Parcel Number
                </span>
                <span className="public-client-booking-confirm-property-value">
                  {propertyDetailsForm.parcelNumber}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Additional Notes */}
      <hr className="public-client-booking-confirm-divider" />
      <div className="public-client-booking-confirm-section public-client-booking-confirm-section--last">
        <div className="public-client-booking-section-title">
          Additional Notes (Optional)
        </div>
        <textarea
          className="form-control public-client-booking-confirm-notes"
          placeholder="Anything else that may help with the inspection (e.g., entrance instructions, lockbox code, etc.)"
          value={notes}
          onChange={(e) => dispatch(setNotes(e.target.value))}
          rows={4}
        />
      </div>

      {/* Confirm Button */}
      <div className="public-client-booking-confirm-action">
        <button
          type="button"
          className="btn btn-primary public-client-booking-confirm-btn"
          onClick={() => dispatch(confirmBooking(uniqueId))}
          disabled={confirmLoading}
        >
          {confirmLoading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Confirming...
            </>
          ) : (
            "Confirm Booking"
          )}
        </button>
      </div>
    </div>
  );
};

export default StepConfirmBooking;
